/**
 * Ficha del documento (DESIGN.md §11.7, maqueta m12), la misma en la ventana y en «Abrir como página» (decisión del
 * dueño, 19-10), en dos mitades:
 * - arriba: «Tipo N° · fecha · materia», título 22/600 y acciones con ícono;
 * - izquierda (470 px por defecto, 320–640 con el divisor, riel de 56 px al plegar): para los documentos del SII, el
 *   panel «Resumen» (`PanelResumen`: frase, vigencia, normas de la cabecera, original, cita a / citado por); para
 *   leyes (m6), convenios y sentencias, la frase y sus plegables de siempre;
 * - derecha, sobre `slate-50`: el texto reconstruido con buscador interno (`TextoDocumento`).
 * Salen (01-11): Criterio del SII, Cómo se aplica, Condiciones, Atributos (SII), Misma materia, Similares y el chat.
 */
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, ChevronDown, ChevronRight, ChevronUp, Copy, ExternalLink, Star, X } from 'lucide-react';
import { Barra, FOCO } from '../biblioteca/piezas';
import { TextoDocumento } from './TextoDocumento';
import PanelResumen, { AbrirOtro, Divisor, RielPanel } from './PanelResumen';
import type { DatosDocumento } from '../../hooks/useDocumento';
import { tieneCadena } from '../../hooks/useDocumento';
import { ANCHO_RIEL, usePanelDocumento } from '../../hooks/usePanelDocumento';
import type { Document } from '../../utils/supabase';
import { getInstitution } from '../../utils/institutions';
import { fechaCorta, fechaLarga } from '../../utils/fechas';
import { tipoSingular } from '../../utils/bibliotecas';
import { tribunalDeRit } from '../../utils/tribunales';
import { citaDocumento, etiquetaDocumento, minutosLectura, pdfEmbebible, Seccion, sitioOrigen, tituloFicha } from '../../utils/fichaDocumento';
import { fraseFila, primeraOracion } from '../../utils/filaDocumento';
import { articulosDelTexto, estadoConvenio, etiquetaNorma, materiasNorma, paisConvenio, tituloNorma } from '../../utils/normasFila';
import { safeExternalUrl } from '../../utils/citationFormat';

const MATERIA_OFICIO: Record<string, string> = { iva: 'IVA', renta: 'Renta', otras: 'Otras materias' };

/** Nombre de la mitad del texto según el tipo: «Texto del oficio», «Texto de la sentencia»… */
function nombreTexto(tipo: string): string {
  const crudo = (tipo || '').toLowerCase();
  if (crudo === 'dl') return 'Texto del decreto ley';
  if (crudo === 'dfl') return 'Texto del DFL';
  const t = tipoSingular(tipo).toLowerCase();
  if (!t) return 'Texto del documento';
  const femenino = ['circular', 'resolución', 'sentencia', 'ley'].includes(t);
  return `Texto ${femenino ? 'de la' : 'del'} ${t}`;
}

function Lista({ items }: { items: string[] }) {
  return items.length === 1
    ? <p className="text-[14px] text-slate-700 leading-relaxed break-words">{items[0]}</p>
    : <ul className="pl-5 space-y-1.5 text-[14px] text-slate-700 list-disc leading-relaxed break-words">{items.map((t, i) => <li key={i}>{t}</li>)}</ul>;
}

/** Plegable de la mitad izquierda (`.sec`): título, vista previa en gris a la derecha y contenido al abrir. */
function Plegable({ titulo, previa, abierto: inicial = false, children }: { titulo: string; previa?: string; abierto?: boolean; children: ReactNode }) {
  const [abierto, setAbierto] = useState(inicial);
  return (
    <div className="border-t border-slate-100">
      <button type="button" onClick={() => setAbierto(a => !a)} aria-expanded={abierto} className={`w-full h-[52px] flex items-center justify-between gap-4 text-left rounded ${FOCO}`}>
        <span className="flex items-center gap-2 text-[14.5px] text-slate-800">
          {abierto ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" />}
          {titulo}
        </span>
        {previa && <span className="text-[13px] text-slate-500 shrink-0 tabular-nums">{previa}</span>}
      </button>
      {abierto && <div className="pb-4 pl-[22px]">{children}</div>}
    </div>
  );
}

/** Secciones del resumen IA que salieron de la ventana (§11.7): criterio, cómo se aplica, condiciones. */
const SECCIONES_FUERA = new Set(['criterio_aplicable', 'alcance_practico', 'condiciones_excepciones']);
const normal = (t: string) => t.replace(/\s+/g, ' ').trim().toLowerCase();
const previa = (s: Seccion) => (s.items.length === 1 ? 'texto completo' : `${s.items.length} puntos`);

export interface FichaDocumentoProps {
  id: string;
  /** `useDocumento(id)`: lo llama quien contiene la ficha (la página usa el documento también en su cabecera). */
  datos: DatosDocumento;
  modo: 'ventana' | 'pagina';
  /** Búsqueda de la lista: se lleva al buscador del texto. */
  consulta?: string;
  /** `#p-k` de la URL: el texto se desplaza a ese párrafo. */
  parrafoInicial?: number | null;
  /** Id del título (la ventana lo usa como nombre accesible del diálogo). */
  idTitulo?: string;
  onCerrar?: () => void;
  onAnterior?: () => void;
  onSiguiente?: () => void;
  /** Abrir otro documento sin salir de la ventana (citas, modificación), opcionalmente en un párrafo. */
  abrirOtro?: AbrirOtro;
}

const ICONO = `w-9 h-9 grid place-items-center rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors motion-reduce:transition-none ${FOCO}`;

export default function FichaDocumento({ id, datos, modo, consulta = '', parrafoInicial = null, idTitulo, onCerrar, onAnterior, onSiguiente, abrirOtro }: FichaDocumentoProps) {
  const { doc, estado, cargar, ficha, isFavorite, favError, favGuardando, toggleFavorite, cadena, cargarCadena, usuarioId } = datos;
  const [copiado, setCopiado] = useState<'quieto' | 'copiado' | 'error'>('quieto');
  const [parrafoActivo, setParrafoActivo] = useState<number | null>(null);
  // El ¶ activo lo manda `TextoDocumento` (se remonta por documento y avisa null o el de «#p-k»).
  useEffect(() => { setCopiado('quieto'); }, [id]);
  const conPanel = !!doc && tieneCadena(doc);
  const { ancho, plegado, setAncho, alternar } = usePanelDocumento(usuarioId, conPanel);
  // Índice por artículo de una ley: se calcula una vez por documento (hay textos de 626 mil caracteres).
  const indiceLey = useMemo(
    () => (doc && doc.institution_code?.toUpperCase() === 'BCN' ? articulosDelTexto(doc.contenido_extraido) : undefined),
    [doc],
  );

  if (estado !== 'listo' || !doc || !ficha) {
    return (
      <div className="h-full flex flex-col">
        {modo === 'ventana' && onCerrar && (
          <div className="flex justify-end px-6 pt-5">
            <button type="button" onClick={onCerrar} aria-label="Cerrar (Esc)" title="Cerrar (Esc)" className={ICONO}><X className="w-[18px] h-[18px]" /></button>
          </div>
        )}
        {estado === 'cargando' ? (
          <div role="status" aria-busy="true" aria-label="Cargando documento" className="flex-1 min-h-0 flex flex-col">
            <div className="px-8 pt-6 pb-5 border-b border-slate-200/70 space-y-3"><Barra ancho="200px" /><Barra ancho="60%" alto="h-6" /></div>
            <div className="flex-1 min-h-0 grid" style={{ gridTemplateColumns: `${plegado ? ANCHO_RIEL : ancho}px minmax(0,1fr)` }}>
              <div className="px-8 py-6 space-y-3 border-r border-slate-200/70"><Barra ancho="92%" alto="h-4" /><Barra ancho="80%" alto="h-4" /><Barra ancho="40%" /><div className="pt-4 space-y-4"><Barra ancho="70%" /><Barra ancho="60%" /><Barra ancho="65%" /></div></div>
              <div className="bg-slate-50 px-8 py-6 space-y-3"><Barra ancho="96%" /><Barra ancho="90%" /><Barra ancho="94%" /><Barra ancho="70%" /></div>
            </div>
          </div>
        ) : (
        <div className="px-8 py-6 space-y-4">
          {estado === 'error' ? (
            <p className="text-[13px] text-slate-500" role="alert">
              No se pudo cargar el documento.{' '}
              <button type="button" onClick={cargar} className={`font-medium text-indigo-700 hover:underline rounded ${FOCO}`}>Reintentar</button>
            </p>
          ) : (
            <div className="text-[13px] text-slate-500 space-y-2">
              <p>Documento no encontrado. Puede que el enlace esté incompleto o que el documento ya no esté en el corpus.</p>
              <Link to="/bibliotecas" className={`inline-flex items-center gap-1 font-medium text-indigo-700 hover:underline rounded ${FOCO}`}>
                <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" /> Volver a bibliotecas
              </Link>
            </div>
          )}
        </div>
        )}
      </div>
    );
  }

  const esLey = doc.institution_code?.toUpperCase() === 'BCN';
  const esConvenio = (doc.tipo || '').toLowerCase() === 'convenio';
  const etiqueta = esLey ? etiquetaNorma(doc) : etiquetaDocumento(doc);
  const titulo = esLey ? tituloNorma(doc) : tituloFicha(doc) || etiqueta;
  const esTta = doc.institution_code?.toUpperCase() === 'TTA';
  const ojv = (() => {
    const leg = (doc as Document & { legislacion?: unknown }).legislacion;
    return Array.isArray(leg) && leg[0] && typeof leg[0] === 'object' ? (leg[0] as { procedimiento?: string; materia?: string }) : {};
  })();
  const materiaCorta = doc.institution_code?.toUpperCase() === 'SII' ? MATERIA_OFICIO[(doc.materia || '').toLowerCase()] : undefined;
  const fecha = fechaCorta(doc.fecha_publicacion);
  const texto = doc.contenido_extraido || '';
  const frase = fraseFila(doc);
  const materias = esLey ? materiasNorma(doc) : [];
  const convenio = esConvenio ? estadoConvenio(doc) : null;
  // Cabecera (m2, m6): oficio «Oficio N° 214 · fecha · Renta»; TTA con tribunal y procedimiento; ley «Ley 21.591 ·
  // publicada el … · primera materia»; convenio «país · Vigente desde …».
  const meta: (string | false | null | undefined)[] = esTta
    ? [etiqueta, fecha, tribunalDeRit(doc.numero)?.nombre, ojv.procedimiento]
    : esLey ? [etiqueta, fecha && `publicada el ${fecha}`, materias[0]]
    : convenio ? [paisConvenio(doc) || 'Convenio', convenio.fecha && `${convenio.rotulo.toLowerCase()} ${fechaCorta(convenio.fecha)}`]
    : [etiqueta, fecha, materiaCorta];

  const atributos: [string, string | undefined | null][] = [
    ['Tipo', tipoSingular(doc.tipo)],
    [esTta ? 'RIT' : 'Número', esTta ? etiqueta.replace(/^RIT /, '') : etiqueta.includes('N°') ? etiqueta.split('N° ')[1] : 'Sin número'],
    ['Institución', getInstitution(doc.institution_code)?.name || doc.institution_code],
    ...(esTta ? [['Tribunal', tribunalDeRit(doc.numero)?.nombre], ['Procedimiento', ojv.procedimiento]] as [string, string | undefined][] : []),
    ['Publicación', fechaLarga(doc.fecha_publicacion)],
    ['Emisión', doc.fecha_emision && doc.fecha_emision !== doc.fecha_publicacion ? fechaLarga(doc.fecha_emision) : null],
    ['Materia', ficha.materia || materiaCorta],
    ...(esTta ? [['Materia según el tribunal', ojv.materia]] as [string, string | undefined][] : []),
    ['Doctrina previa', ficha.doctrinaPrevia],
  ];
  const visibles = atributos.filter(([, v]) => v);

  const copiar = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('sin portapapeles');
      await navigator.clipboard.writeText(citaDocumento(doc, parrafoActivo));
      setCopiado('copiado');
    } catch {
      setCopiado('error');
    }
    setTimeout(() => setCopiado('quieto'), 1800);
  };
  // En una ley la frase de la ventana es la primera oración del resumen (m6); la síntesis entera queda en su plegable.
  const fraseTexto = frase && !frase.sinSintesis ? (esLey ? primeraOracion(frase.texto) : frase.texto) : '';
  const fraseEsSintesis = !!fraseTexto && normal(fraseTexto) === normal(ficha.sintesis);
  const conFrase = !!fraseTexto;
  const queEstablece = esLey ? [...ficha.tarjetas, ...ficha.desplegables].find(s => s.clave === 'puntos_clave') : undefined;
  const secciones = [...ficha.tarjetas, ...ficha.desplegables].filter(s => !SECCIONES_FUERA.has(s.clave) && s !== queEstablece);
  const origen = safeExternalUrl(doc.source_url);
  const anchoIzquierda = conPanel && plegado ? ANCHO_RIEL : ancho;

  return (
    <div className="h-full flex flex-col min-h-0">
      {/* Cabecera: qué es, título y acciones con ícono */}
      <header className="shrink-0 px-8 pt-6 pb-5 border-b border-slate-200/70 flex items-start justify-between gap-6">
        <div className="min-w-0">
          <div className="text-[13px] text-slate-500 tabular-nums flex flex-wrap gap-x-2">
            {meta.filter(Boolean).map((m, i) => <span key={i} className={i === 0 ? 'text-slate-700 font-medium' : ''}>{i > 0 && <span aria-hidden="true">· </span>}{m}</span>)}
          </div>
          <h1 id={idTitulo} className="mt-2 text-[22px] font-semibold leading-[1.3] tracking-[-0.012em] text-slate-900 max-w-[860px] break-words">{titulo}</h1>
        </div>
        <div className="flex items-center gap-0.5 shrink-0 -mr-2" role="toolbar" aria-label="Acciones del documento">
          {modo === 'ventana' && (onAnterior || onSiguiente) && (
            <>
              <button type="button" onClick={onAnterior} disabled={!onAnterior} aria-label="Documento anterior" title="Documento anterior" className={ICONO}><ChevronUp className="w-[18px] h-[18px]" /></button>
              <button type="button" onClick={onSiguiente} disabled={!onSiguiente} aria-label="Documento siguiente" title="Documento siguiente" className={ICONO}><ChevronDown className="w-[18px] h-[18px]" /></button>
              <span className="w-px h-5 bg-slate-200 mx-2" aria-hidden="true" />
            </>
          )}
          <button
            type="button"
            onClick={toggleFavorite}
            aria-pressed={isFavorite}
            aria-busy={favGuardando}
            disabled={favGuardando}
            aria-label={favError ? 'No se guardó el favorito; inténtalo de nuevo' : 'Guardar en favoritos'}
            title={favError ? 'No se pudo guardar; inténtalo de nuevo' : isFavorite ? 'Quitar de favoritos' : 'Guardar en favoritos'}
            className={`${ICONO} ${favError ? 'text-red-700' : ''}`}
          >
            <Star className={`w-[18px] h-[18px] ${isFavorite ? 'fill-indigo-600 text-indigo-600' : ''}`} />
          </button>
          <button type="button" onClick={copiar} aria-label="Copiar cita" title={copiado === 'copiado' ? 'Cita copiada' : copiado === 'error' ? 'No se pudo copiar' : 'Copiar cita'} className={ICONO}>
            {copiado === 'copiado' ? <Check className="w-[18px] h-[18px] text-emerald-600" /> : <Copy className="w-[18px] h-[18px]" />}
          </button>
          <span className="sr-only" role="status">{copiado === 'copiado' ? 'Cita copiada' : copiado === 'error' ? 'No se pudo copiar la cita' : ''}</span>
          {modo === 'ventana' && (
            <Link to={`/documento/${id}`} aria-label="Abrir como página" title="Abrir como página" className={ICONO}><ExternalLink className="w-[18px] h-[18px]" /></Link>
          )}
          {modo === 'ventana' && onCerrar && (
            <>
              <span className="w-px h-5 bg-slate-200 mx-2" aria-hidden="true" />
              <button type="button" onClick={onCerrar} aria-label="Cerrar (Esc)" title="Cerrar (Esc)" className={ICONO}><X className="w-[18px] h-[18px]" /></button>
            </>
          )}
        </div>
      </header>

      {/* Cuerpo en dos mitades: resumen (o riel) | texto con buscador */}
      <div className="flex-1 min-h-0 grid" style={{ gridTemplateColumns: `${anchoIzquierda}px minmax(0,1fr)` }}>
        <div className="relative min-h-0 border-r border-slate-200/70">
          {conPanel ? (
            plegado ? (
              <RielPanel cadena={cadena} origenRespaldo={origen} onDesplegar={alternar} />
            ) : (
              <PanelResumen
                etiqueta={etiqueta.replace(' N° ', ' ')}
                fecha={fecha || (doc.year ? String(doc.year) : '')}
                frase={fraseTexto}
                cadena={cadena}
                reintentar={cargarCadena}
                origenRespaldo={origen}
                abrir={abrirOtro}
                onPlegar={alternar}
              />
            )
          ) : (
            <div className="h-full min-h-0 overflow-y-auto px-8 py-6">
              {/* La frase arriba en 17/500; si es la misma síntesis (documentos sin criterio), el plegable «Síntesis» no la repite */}
              {conFrase && (
                <p className="text-[17px] font-medium leading-[1.45] text-slate-900 mb-6">{fraseTexto}</p>
              )}

              {/* Ley (m6): «Qué establece» (los puntos clave con su artículo) y las materias como enlaces de texto */}
              {queEstablece && (
                <section className="mb-6">
                  <h2 className="text-[13px] text-slate-500 mb-1.5">Qué establece</h2>
                  <ul className="pl-5 space-y-2 text-[14px] text-slate-700 list-disc marker:text-slate-400 leading-relaxed">{queEstablece.items.map((t, i) => <li key={i}>{t}</li>)}</ul>
                </section>
              )}
              {materias.length > 0 && (
                <section className="mb-6">
                  <h2 className="text-[13px] text-slate-500 mb-1.5">Materias</h2>
                  <p className="text-[14px] leading-relaxed">
                    {materias.map((m, i) => (
                      <span key={m}>
                        {i > 0 && <span className="text-slate-400" aria-hidden="true"> · </span>}
                        <Link to={`/biblioteca/ley?q=${encodeURIComponent(m)}`} className={`text-slate-700 underline decoration-slate-300 underline-offset-4 hover:text-indigo-700 hover:decoration-indigo-300 rounded ${FOCO}`}>{m}</Link>
                      </span>
                    ))}
                  </p>
                </section>
              )}

              {!esLey && ficha.referencias.length > 0 && (
                <section className="mb-6">
                  <h2 className="text-[13px] text-slate-500 mb-1.5">Normas citadas</h2>
                  <p className="text-[14px] text-slate-700 leading-relaxed">
                    {ficha.referencias.map((r, i) => <span key={r}>{i > 0 && <span className="text-slate-400" aria-hidden="true"> · </span>}{r}</span>)}
                  </p>
                </section>
              )}

              {/* Sin nada encima, el primer plegable no lleva línea superior */}
              <div className={`border-b border-slate-100 ${conFrase || ficha.referencias.length ? '' : '[&>div:first-child]:border-t-0'}`}>
                {!fraseEsSintesis && (
                  <Plegable titulo="Síntesis" abierto={!esLey}>
                    <p className="text-[14px] text-slate-700 leading-relaxed break-words">{ficha.sintesis}</p>
                  </Plegable>
                )}
                {secciones.map(s => (
                  <Plegable key={s.clave} titulo={s.titulo} previa={previa(s)}>
                    <Lista items={s.items} />
                  </Plegable>
                ))}
                {esLey && ficha.referencias.length > 0 && (
                  <Plegable titulo="Normas que cita" previa={ficha.referencias.length === 1 ? '1 norma' : `${ficha.referencias.length} normas`}><Lista items={ficha.referencias} /></Plegable>
                )}
                {visibles.length > 0 && (
                  <Plegable titulo="Datos del documento" previa={`${visibles.length} datos`}>
                    <dl className="divide-y divide-slate-100 text-[13px]">
                      {visibles.map(([k, v]) => (
                        <div key={k} className="py-1.5 flex justify-between gap-3">
                          <dt className="text-slate-500 shrink-0">{k}</dt>
                          <dd className="text-slate-800 text-right break-words min-w-0">{v}</dd>
                        </div>
                      ))}
                    </dl>
                  </Plegable>
                )}
              </div>
            </div>
          )}
          {conPanel && !plegado && <Divisor ancho={ancho} onAncho={setAncho} />}
        </div>

        <TextoDocumento
          key={doc.id}
          texto={texto}
          titulo={titulo}
          nombreTexto={nombreTexto(doc.tipo)}
          minutos={minutosLectura(texto)}
          consultaInicial={consulta}
          pdf={pdfEmbebible(doc.source_url)}
          origen={origen}
          sitio={sitioOrigen(doc.source_url)}
          indice={indiceLey}
          placeholder={esLey ? 'Buscar en la ley' : 'Buscar en el texto'}
          centrado={conPanel && plegado}
          parrafoInicial={parrafoInicial}
          onParrafoActivo={setParrafoActivo}
        />
      </div>
    </div>
  );
}
